import { CircularProgress, MenuItem, Select } from "@mui/material";
import React, { useEffect, useState } from "react";
import { getSettingPreferences } from "../../../services/orgService";
import { DatePicker } from "antd";
import { Moment } from "moment";
import { toastMessage } from "../../../helper";
import { successMessage } from "../../../constants/messageConstants";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generateSupplierReport } from "../../../services/srmService";

const SupplierReport = () => {
  const { t, i18n } = useTranslation();
  type RangeValue = [Moment | null, Moment | null] | null;
  const { RangePicker } = DatePicker;
  const reprotTypes = ["Summary Report", "Detailed Report"];
  const intervals = ["Monthly", "Quarterly", "Yearly"];
  const monthFormat = "YYYY/MM";

  const [formData, setFormData] = useState<any>({
    reportType: "Summary Report",
    interval: "Monthly",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [monthlyDates, setMonthlyDates] = useState<any>([null, null]);
  const [quarterlyDates, setQuarterlyDates] = useState<any>([null, null]);
  const [yearlyDates, setYearlyDates] = useState<any>([null, null]);
  const [defaultDateRange, setDefaultRange] = useState<any>([null, null]);
  const [showDateRange, setShowDateRange] = useState(false);

  const onChangeType = (event: any) => {
    if (event?.target?.value?.length) {
      setFormData({
        ...formData,
        reportType: event.target.value,
      });
    }
  };

  const onChangeInterval = (event: any) => {
    if (event?.target?.value?.length) {
      setFormData({
        ...formData,
        interval: event.target.value,
      });
    }
  };

  const validForm = () => {
    return (
      formData.reportType &&
      formData.interval &&
      (formData.interval === "Monthly"
        ? monthlyDates && monthlyDates[0] && monthlyDates[1]
        : formData.interval === "Quarterly"
        ? quarterlyDates && quarterlyDates[0] && quarterlyDates[1]
        : yearlyDates && yearlyDates[0] && yearlyDates[1])
    );
  };

  const getReportDateRange = () => {
    const offsetMonths =
      formData.interval === "Monthly" ? 0 : defaultDateRange[0].$M;
    const intervalOffsetMonths =
      formData.interval === "Monthly"
        ? 1
        : formData.interval === "Quarterly"
        ? 3
        : 12;
    const selectedInterval =
      formData.interval === "Monthly"
        ? monthlyDates
        : formData.interval === "Quarterly"
        ? quarterlyDates
        : yearlyDates;
    const startMonth = new Date(
      selectedInterval[0].$y,
      selectedInterval[0].$M + offsetMonths,
      2
    );
    const endMonth = new Date(
      selectedInterval[1].$y,
      selectedInterval[1].$M + offsetMonths + intervalOffsetMonths,
      1
    );
    return [startMonth, endMonth, offsetMonths];
  };

  const downloadReport = () => {
    const [startMonth, endMonth, offsetMonths] = getReportDateRange();
    setIsLoading(true);
    let reqObj: any = {
      reportType: formData.reportType,
      interval: formData.interval,
      fromDate: startMonth,
      toDate: endMonth,
      offsetMonths,
    };
    generateSupplierReport(reqObj)
      .then(async (res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Supplier-Report.xlsx");
        document.body.appendChild(link);
        link.click();
        toastMessage(successMessage().GENERATE_SUCCESS, "success");
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      });
  };

  const setDefaultDateRange = (companyData: any) => {
    let startDate, endDate;
    if (companyData.is_calendar_year) {
      startDate = dayjs(new Date().getFullYear() + "/01/01", monthFormat);
      endDate = dayjs(new Date().getFullYear() + "/12/31", monthFormat);
    } else {
      if (companyData.financial_year) {
        const dateRange = companyData.financial_year.split("to");
        let startMonth =
          (dateRange[0].trim().length === 1 ? "0" : "") + dateRange[0].trim();
        let endMonth =
          (dateRange[1].trim().length === 1 ? "0" : "") + dateRange[1].trim();

        startDate = dayjs(
          new Date().getFullYear() + "/" + startMonth + "/01",
          monthFormat
        );
        endDate = dayjs(
          new Date().getFullYear() +
            1 +
            "/" +
            endMonth +
            "/" +
            new Date(
              new Date().getFullYear() + 1,
              Number(endMonth),
              0
            ).getDate() +
            1,
          monthFormat
        );
      }
    }
    setDefaultRange([startDate, endDate]);
    setMonthlyDates([startDate, endDate]);
    setQuarterlyDates([
      dayjs(new Date().getFullYear() + "/01/01", monthFormat),
      dayjs(new Date().getFullYear() + "/10/01", monthFormat),
    ]);
    setYearlyDates([
      dayjs(new Date().getFullYear() + "/01/01", monthFormat),
      dayjs(new Date().getFullYear() + "/01/01", monthFormat),
    ]);
    setShowDateRange(true);
  };

  useEffect(() => {
    getSettingPreferences(true).then((res) => {
      if (res?.data?.companyPreference) {
        setDefaultDateRange(res.data.companyPreference);
      }
    });
  }, []);

  return (
    <div className="flex justify-center items-center h-full bg-neutral-100 p-10 overflow-y-auto">
      <div className="flex pw-hide-scroll flex-col pw-report-main bg-white overflow-y-auto p-5">
        <div className="pw-report-field">
          <p className={`pw-usetiful-management pw-report-label`}>
            {t("Type of report")}{" "}
          </p>
          <Select
            label={formData.reportType}
            className="pw-report-select pw-e2e-reporttype"
            value={formData.reportType}
            onChange={onChangeType}
          >
            {reprotTypes.map((eachType: any) => (
              <MenuItem value={eachType} key={eachType}>
                {eachType}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="pw-report-field">
          <p className={`pw-usetiful-management pw-report-label`}>
            {t("Interval")}{" "}
          </p>
          <Select
            label={formData.interval}
            className="pw-report-select pw-e2e-reporttype"
            value={formData.interval}
            onChange={onChangeInterval}
          >
            {intervals.map((eachInterval: any) => (
              <MenuItem value={eachInterval} key={eachInterval}>
                {eachInterval}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="pw-report-field">
          <p className="pw-report-label">{t("Duration")}</p>
          {showDateRange &&
            (formData.interval === "Monthly" ? (
              <RangePicker
                className="pw-report-select"
                picker="month"
                onCalendarChange={(val: any) => {
                  setMonthlyDates(val);
                }}
                value={monthlyDates}
                format={monthFormat}
              />
            ) : formData.interval === "Quarterly" ? (
              <RangePicker
                className="pw-report-select"
                picker="quarter"
                onCalendarChange={(val: any) => {
                  setQuarterlyDates(val);
                }}
                value={quarterlyDates}
              />
            ) : (
              <RangePicker
                className="pw-report-select"
                picker="year"
                onCalendarChange={(val: any) => {
                  setYearlyDates(val);
                }}
                value={yearlyDates}
              />
            ))}
        </div>
        <div className="flex justify-end pt-2 pr-2">
          <div
            className={`!min-w-[6rem] pw-bunit-button-save text-white  
           ${
             validForm()
               ? isLoading
                 ? "text-black bg-neutral-300"
                 : "cursor-pointer bg-primary"
               : "text-black bg-neutral-300"
           }`}
            onClick={() => validForm() && !isLoading && downloadReport()}
          >
            {isLoading ? (
              <div className="flex flex-row gap-2 p-2">
                <span>{t("Downloading")}</span>
                <CircularProgress size={20} />
              </div>
            ) : (
              <span>{t("Download")}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierReport;
